'use client';

import { c as _c } from "react/compiler-runtime";
import { FormControl, FormDescription, FormItem, FormLabel, FormMessage } from '../../components';
import type { ReactNode } from 'react';
import type { ControllerFieldState, FieldValues } from 'react-hook-form';
import type { FormFieldConfig } from '../types';
import type { BaseFieldProps } from './types';
import type { FieldIds } from './utils';
interface FieldShellProps<TFieldValues extends FieldValues> {
  fieldConfig: FormFieldConfig<TFieldValues>;
  fieldState: ControllerFieldState;
  /** IDs and aria attributes from `getFieldIds` */
  ids: FieldIds;
  labelSize?: BaseFieldProps<TFieldValues>['labelSize'];
  /** Overrides the default `flex-1` on the wrapping `FormItem` */
  className?: string;
  /**
   * When false, `children` are rendered directly and are responsible for placing
   * their own `<FormControl>`. Needed by fields whose control must sit inside a
   * provider-style wrapper (e.g. `SelectField`'s `<Select>`).
   */
  withFormControl?: boolean;
  children: ReactNode;
}

/**
 * Shared layout for declarative form fields: an optional label above the
 * control, then the control, then the description and validation message.
 *
 * Centralizes the `FormItem` / `FormLabel` / `FormControl` / `FormDescription` /
 * `FormMessage` scaffold — and its aria wiring — so individual field components
 * only describe their own input.
 *
 * Fields whose label sits inline with the control (`CheckboxField`,
 * `SwitchField`) or whose description sits above it
 * (`MultiSelectCheckboxField`) build their own layout instead.
 *
 * @example
 * ```tsx
 * const ids = getFieldIds(fieldConfig, fieldState, id);
 *
 * <FieldShell fieldConfig={fieldConfig} fieldState={fieldState} ids={ids} labelSize={labelSize}>
 *   <Input aria-describedby={ids.describedBy} aria-invalid={ids.isInvalid} id={ids.fieldId} />
 * </FieldShell>
 * ```
 */
export function FieldShell(t0) {
  const $ = _c(22);
  const {
    fieldConfig,
    fieldState,
    ids,
    labelSize,
    className: t1,
    withFormControl: t2,
    children
  } = t0;
  const className = t1 === undefined ? "flex-1" : t1;
  const withFormControl = t2 === undefined ? true : t2;
  const {
    fieldId,
    labelId,
    descriptionId,
    errorId
  } = ids;
  let t3;
  if ($[0] !== fieldConfig.label || $[1] !== fieldConfig.labelHidden || $[2] !== fieldConfig.required || $[3] !== fieldId || $[4] !== labelId || $[5] !== labelSize) {
    t3 = fieldConfig.label && <FormLabel className={fieldConfig.labelHidden ? "sr-only" : undefined} htmlFor={fieldId} id={labelId} required={fieldConfig.required} size={labelSize} weight="medium">{fieldConfig.label}</FormLabel>;
    $[0] = fieldConfig.label;
    $[1] = fieldConfig.labelHidden;
    $[2] = fieldConfig.required;
    $[3] = fieldId;
    $[4] = labelId;
    $[5] = labelSize;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== withFormControl) {
    t4 = withFormControl ? <FormControl>{children}</FormControl> : children;
    $[7] = children;
    $[8] = withFormControl;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== descriptionId || $[11] !== fieldConfig.description) {
    t5 = descriptionId && <FormDescription id={descriptionId}>{fieldConfig.description}</FormDescription>;
    $[10] = descriptionId;
    $[11] = fieldConfig.description;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== errorId || $[14] !== fieldState) {
    t6 = errorId && <FormMessage id={errorId}>{fieldState.error?.message}</FormMessage>;
    $[13] = errorId;
    $[14] = fieldState;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== className || $[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = <FormItem className={className}>{t3}{t4}{t5}{t6}</FormItem>;
    $[16] = className;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}